import { API_URLS } from "../config";

// Shared in-flight refresh so concurrent 401s only trigger one refresh call.
let refreshPromise = null;

const refreshAccessToken = async () => {
  const refreshToken = localStorage.getItem("refreshToken");
  if (!refreshToken) return null;

  try {
    const res = await fetch(API_URLS.REFRESH_TOKEN, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    });
    if (!res.ok) return null;

    const data = await res.json().catch(() => ({}));
    if (!data.accessToken) return null;

    localStorage.setItem("token", data.accessToken);
    window.dispatchEvent(new CustomEvent("auth:token-refreshed", { detail: { accessToken: data.accessToken } }));
    return data.accessToken;
  } catch {
    return null;
  }
};

const clearSessionAndRedirect = () => {
  ["token", "user", "refreshToken", "allowedPages", "allowedApis"].forEach(k => localStorage.removeItem(k));
  window.location.href = "/";
};

/**
 * Unified API helper — handles JSON and FormData.
 * On 401, silently refreshes the access token and retries once before
 * giving up; only clears the session and redirects to login if the
 * refresh token itself is missing/invalid.
 * Returns { ok, data, denied, message } (null only if the session was cleared).
 */
export const apiRequest = async (url, options = {}, token = null, _isRetry = false) => {
  try {
    const { method = "GET", body, isFormData = false, headers: extraHeaders = {} } = options;

    const headers = { ...extraHeaders };
    if (token) headers["Authorization"] = `Bearer ${token}`;

    let finalBody;
    if (body) {
      if (isFormData || body instanceof FormData) {
        finalBody = body;
        // Do NOT set Content-Type — browser sets multipart boundary automatically
      } else {
        headers["Content-Type"] = "application/json";
        finalBody = JSON.stringify(body);
      }
    }

    const res = await fetch(url, { method, headers, body: finalBody });

    if (res.status === 401) {
      if (!_isRetry) {
        if (!refreshPromise) {
          refreshPromise = refreshAccessToken().finally(() => { refreshPromise = null; });
        }
        const newToken = await refreshPromise;
        if (newToken) {
          return apiRequest(url, options, newToken, true);
        }
      }
      clearSessionAndRedirect();
      return null;
    }

    if (res.status === 403) {
      const data = await res.json().catch(() => ({}));
      return { ok: false, denied: true, message: data.message || "Access denied.", data };
    }

    const data = await res.json().catch(() => ({}));
    return { ok: res.ok, data, status: res.status };

  } catch (err) {
    console.error("apiRequest error:", err);
    return { ok: false, data: { message: "Network error. Check your connection." } };
  }
};